/**
 * Slice 43 — which controls a published draft offers, and the words a result
 * is drawn with. Decisions only: no React, no API client, so
 * `verify-meta-ads-controls-screen` runs every branch without a browser.
 *
 * ## OFFERED FROM META'S READ, NEVER FROM WEND'S RECORD
 *
 * `record.delivery` is what Wend last did. Whether Start or Pause is offered is
 * decided from `MetaAdDeliveryState` — what Meta says now — because somebody
 * may have started or paused the campaign in Ads Manager since. And the reasons
 * a start is refused come from `startBlockers`, the function the API calls
 * before it starts anything: for one read, a button this page offers is never
 * one the API refuses for one of those reasons.
 *
 * ## AND SINCE SLICE 52, TWO MORE THINGS ARE READ RATHER THAN REMEMBERED
 *
 * **Where the money is.** Wend's Budget control writes the AD SET's field.
 * Somebody moving the budget to the campaign in Ads Manager used to leave the
 * ad set's null, which withheld Start and drew a badge saying Wend could not
 * read it. Both levels are read now, `metaDeliveryBudget` says which is in
 * force, and the Budget control is offered only when that is the ad set —
 * otherwise `adsManager` is, because Ads Manager is where a campaign budget
 * can be changed and a person who has just read "Campaign budget per day"
 * should not have to find the door.
 *
 * **Whether it has been switched on before.** `record.startedAt` is Wend's own
 * first confirmed start, and for a campaign somebody has been running in Ads
 * Manager it is null — so Wend's "first" start would have switched ON every
 * ad, including one switched off there for a reason. `metaSwitchedOnBefore` is
 * the record OR what Meta says, so `switched_off` reaches such a campaign and
 * the confirmation counts the ads a start would actually leave running.
 *
 * ## THE BRAKE IS OFFERED WHEN IN DOUBT
 *
 * When Meta could not be read, or would not say what the token may do,
 * nothing is known — so Start, the budget and the cap are withdrawn, and Pause
 * is not. Pausing a campaign that turns out to be paused already costs
 * nothing; not offering the brake on a campaign that turns out to be running
 * costs money. It is withdrawn only where the API could not act on it at all:
 * a token Meta says may not manage ads, a campaign in another ad account, no
 * connection — and there the page offers Ads Manager instead.
 *
 * ## A STOPPED PAUSE IS ALWAYS AN ALARM
 *
 * Whatever stopped it — Meta refused, never answered, or the connection is
 * broken — a pause that did not happen leaves a running campaign running. So
 * its tone is `bad` whatever `stop` says, and so is a start whose pausing-again
 * failed; every other stopped change is drawn by what `stop` says it means.
 */
import type { CalloutTone } from '@wend/ui';

/* `@wend/shared` directly, never through the API client. */
import {
  metaDeliveryBudget,
  metaSwitchedOnBefore,
  metaTypedAmountSchema,
  startBlockers,
  typedAmountToMinor,
  type MetaAdControlAction,
  type MetaAdControlResult,
  type MetaAdControlStop,
  type MetaAdDelivery,
  type MetaAdDeliveryState,
  type MetaAdDraftRecord,
  type MetaAdsGrant,
  type MetaAdsSurfaceFigures,
} from '@wend/shared';

export interface ControlsOffered {
  readonly start: boolean;
  readonly pause: boolean;
  readonly budget: boolean;
  readonly spendCap: boolean;
  /**
   * The link to Ads Manager. Offered where Wend cannot act at all — the brake
   * of last resort — and, since slice 52, wherever the budget is somewhere
   * Wend's own budget control does not write: the campaign level, both levels
   * at once, or neither readable. In each of those the place to change it is
   * Ads Manager, and a person who has just read "Campaign budget per day"
   * should not have to find the door themselves.
   */
  readonly adsManager: boolean;
}

const NONE: ControlsOffered = { start: false, pause: false, budget: false, spendCap: false, adsManager: true };
const BRAKE_ONLY: ControlsOffered = { ...NONE, pause: true, adsManager: false };

/**
 * What this page may offer. `grant` is the connection's, as the API sent it —
 * see `lib/api/meta-ads.ts` for why it is never derived here — and the rules
 * are the API's: a token Meta described without `ads_management` gets nothing,
 * because Meta refuses all four; a token Meta would not describe gets the
 * brake, because a refused pause costs nothing and a withheld one can.
 *
 * `'unread'` is this page's own request for the read failing — as much in
 * doubt as a read Meta refused, and never drawn from the read before it.
 */
export function controlsOffered(
  record: MetaAdDraftRecord,
  state: MetaAdDeliveryState | 'unread' | null,
  grant: MetaAdsGrant,
  now: Date,
): ControlsOffered {
  if (record.status !== 'published' || state === null) return NONE;
  if (grant.known && !grant.canManage) return NONE;
  if (state === 'unread') return BRAKE_ONLY;
  switch (state.state) {
    case 'failed':
    case 'not_checked':
      return BRAKE_ONLY;
    case 'not_published':
    case 'not_connected':
    case 'not_permitted':
    case 'elsewhere':
      return NONE;
    case 'ok':
      break;
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
  if (!grant.known) return BRAKE_ONLY;
  const { delivery } = state;
  const running = delivery.campaign.running;
  /*
    Slice 52. Two readings, both from Meta rather than from Wend's record.

    `switchedOnBefore` decides whether a start would switch every ad on, so it
    decides `switched_off` — and for a campaign somebody has been running in
    Ads Manager, Wend's own `startedAt` is null and would have said "first
    start" about a campaign that has been spending for a week.

    The Budget control writes the AD SET's `daily_budget`. It is offered only
    when that is where the money actually is: a campaign-level budget floors
    against the campaign's spend, not the ad set's — a distinction slice 43 was
    corrected to keep — and writing the ad set's while the campaign's is in
    force would set a figure that does nothing. Two levels at once is the same
    answer for a stronger reason: nothing on screen can say which one spends.
    No sentence says so, because the figure's own label already named the
    level; the button is simply not drawn, and Ads Manager is.
  */
  const switchedOnBefore = metaSwitchedOnBefore(record, delivery);
  const onAdSet = metaDeliveryBudget(delivery)?.level === 'ad_set';
  const start = !running && startBlockers(delivery, now, switchedOnBefore).length === 0;
  const pause = running;
  return {
    start,
    pause,
    budget: onAdSet,
    spendCap: true,
    /*
      Slice 52 added the budget's half of this and took away the other half by
      accident: `!onAdSet` alone withdrew the link from every campaign whose
      budget Wend can write — including the ones where Wend can do nothing
      else. A blocked start is exactly that case. `switched_off`,
      `creative_changed`, `other_ad_sets`, `campaign_gone` and `video_failed`
      all say, in their own words, that the campaign is to be started in Ads
      Manager; a person who has just read that sentence should not then have to
      find the door themselves, and the budget the ad set happens to hold has
      nothing to do with it. So: wherever the budget is not Wend's to write, or
      wherever neither of Wend's two switches is offered.
    */
    adsManager: !onAdSet || (!start && !pause),
  };
}

/**
 * How many of the draft's ads a start would leave running: all of them on a
 * first switch-on, which switches them on; afterwards, those still switched on
 * — a later start flips the campaign alone.
 *
 * `switchedOnBefore` is {@link metaSwitchedOnBefore}, not Wend's `startedAt`:
 * a campaign already running at Meta is not about to have every one of its ads
 * switched on by a press here, so the confirmation must not count them as ads
 * that a start would run.
 */
export function adsAStartRuns(delivery: MetaAdDelivery, switchedOnBefore: boolean): number {
  return switchedOnBefore ? delivery.ads.filter((ad) => ad.running).length : delivery.ads.length;
}

/**
 * Whether Meta answered a results read with nothing at all, as against with
 * zeros — slice 52, gap 3.
 *
 * "We have not been told" and "nothing happened" must never render alike, and
 * this is the line between them: a read with no placement rows and no figure
 * measured on the whole is Meta saying nothing, which is one "Nothing
 * reported" chip. A read whose figures are zeros is Meta saying the campaign
 * did nothing, which is `0` in each figure. A zero is never folded in here —
 * only `null`, which is what the contract uses for a figure Meta did not
 * measure.
 *
 * The contract has no arm for this, so it is decided from the read's own
 * nulls. That is deliberate rather than convenient: the alternative was four
 * "Not measured" chips in a grid, which is four chips for one fact and reads
 * as four separate failures.
 */
export function metaResultsEmpty(results: MetaAdsSurfaceFigures): boolean {
  const { figures } = results;
  return (
    results.byPlacement.length === 0 &&
    figures.spend === null &&
    figures.impressions === null &&
    figures.reach === null &&
    figures.clicks === null
  );
}

/**
 * A typed amount → basic units in the currency the read reported, or the
 * sentence the field shows. The door the API's own conversion uses, so the
 * two refuse alike.
 */
export function typedAmount(
  typed: string,
  currency: string,
  what: string,
): { readonly ok: true; readonly minor: number } | { readonly ok: false; readonly message: string } {
  const parsed = metaTypedAmountSchema.safeParse(typed);
  if (!parsed.success) {
    return { ok: false, message: parsed.error.issues[0]?.message ?? 'Write the amount as a number.' };
  }
  return typedAmountToMinor(parsed.data, currency, currency, what);
}

export const ACTION_LABEL: Readonly<Record<MetaAdControlAction, string>> = {
  start: 'Start',
  pause: 'Pause',
  budget: 'Budget',
  spend_cap: 'Spend cap',
};

/** Each stop as the history labels it. "Not made" is true of every refusal: Meta's, and Wend's own. */
export const STOP_LABEL: Readonly<Record<MetaAdControlStop, string>> = {
  refused: 'Not made',
  unconfirmed: 'Not confirmed',
  preempted: 'Overtaken',
  pause_failed: 'Pause failed',
};

/**
 * A stopped change's tone, for its callout and its history badge — see the
 * header for why a pause, and a start whose pausing-again failed, is always
 * `bad`.
 */
export function stoppedTone(action: MetaAdControlAction, stop: MetaAdControlStop | null): CalloutTone {
  if (action === 'pause' || stop === 'pause_failed') return 'bad';
  switch (stop) {
    case 'unconfirmed':
      return 'warn';
    case 'preempted':
      return 'info';
    default:
      return 'bad';
  }
}

/** A done change's toast. A removed cap is said as removed, not as "changed". */
export function doneTitle(action: MetaAdControlAction, result: MetaAdControlResult): string {
  switch (action) {
    case 'start':
      return 'Started';
    case 'pause':
      return 'Paused';
    case 'budget':
      return 'Budget changed';
    case 'spend_cap':
      return result.record.spendCap === null ? 'Spend cap removed' : 'Spend cap changed';
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

/**
 * Where to stop a campaign Wend cannot act on — the brake of last resort. Ads
 * Manager, opened on the account the campaign is in.
 */
export function adsManagerUrl(adAccountId: string, campaignId: string | null): string {
  const url = new URL('https://adsmanager.facebook.com/adsmanager/manage/campaigns');
  url.searchParams.set('act', adAccountId);
  if (campaignId !== null) url.searchParams.set('selected_campaign_ids', campaignId);
  return url.toString();
}
